<template>
  <div class="sync-record-container">
    <!-- 同步记录列表 -->
    <canvas-glass-card title="同步记录" variant="light" :hoverable="false" class="table-card">
      <template #header>
        <a-row>
          <a-col flex="auto">
            <a-form :model="searchForm" layout="inline" @submit.prevent="handleSearch">
              <a-form-item field="taskName" label="任务名称">
                <a-select 
                  v-model="searchForm.taskName" 
                  :options="taskNameOptions"
                  :field-names="{ value: 'taskName', label: 'taskName' }"
                  placeholder="请选择任务名称" 
                  allow-clear
                  allow-search
                  @press-enter="handleSearch"
                  style="width: 200px;"
                />
              </a-form-item>
              <a-form-item>
                <a-button type="primary" @click="handleSearch">
                  <template #icon>
                    <icon-search />
                  </template>
                  查询
                </a-button>
                <a-button @click="resetSearch" style="margin-left: 10px;">
                  重置
                </a-button>
              </a-form-item>
            </a-form>
          </a-col>
          <a-col flex="120px">
            <a-button type="primary" @click="fetchSyncRecordList">
              <template #icon>
                <icon-refresh />
              </template>
              刷新
            </a-button>
          </a-col>
        </a-row>
      </template>

      <!-- 同步记录表格 -->
      <a-table
        row-key="id"
        :columns="tableColumns"
        :data="syncRecordList"
        :pagination="pagination"
        :loading="loading"
        column-resizable
        stripe
        :bordered="{cell:true}"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      >
        <template #status="{ record }">
          <a-tag v-if="record.status === 'RUNNING'" color="blue">运行中</a-tag>
          <a-tag v-else-if="record.status === 'SUCCESS'" color="green">成功</a-tag>
          <a-tag v-else-if="record.status === 'FAILED'" color="red">失败</a-tag>
          <a-tag v-else>{{ record.status }}</a-tag>
        </template>
        <template #duration="{ record }">
          <span v-if="record.startTime && record.endTime">
            {{ ((record.endTime - record.startTime) / 1000).toFixed(2) }}秒
          </span>
          <span v-else>-</span>
        </template>
        <template #action="{ record }">
          <a-space>
            <a-button type="text" size="mini" @click="viewDetail(record)" class="button-table-option">
              <template #icon>
                <icon-eye />
              </template>
              查看详情
            </a-button>
          </a-space>
        </template>
      </a-table>
    </canvas-glass-card>

    <!-- 详情弹窗 -->
    <a-modal 
      v-model:visible="detailVisible" 
      :title="`同步记录详情 - ${currentRecord.taskName}`"
      width="90%"
      @ok="detailVisible = false"
      @cancel="detailVisible = false"
      :body-style="{ padding: 0 }"
    >
      <a-spin :loading="detailLoading" class="spin-container">
        <div class="detail-container" v-if="!detailLoading">
          <a-row :gutter="20" class="detail-row">
            <a-col :span="4" class="detail-left">
              <a-descriptions
                size="medium"
                :column="1"
                bordered
              >
                <a-descriptions-item label="任务名称">
                  {{ currentRecord.taskName }}
                </a-descriptions-item>
                <a-descriptions-item label="表名">
                  {{ currentRecord.tableName }}
                </a-descriptions-item>
                <a-descriptions-item label="状态">
                  <a-tag v-if="currentRecord.status === 'RUNNING'" color="blue">运行中</a-tag>
                  <a-tag v-else-if="currentRecord.status === 'SUCCESS'" color="green">成功</a-tag>
                  <a-tag v-else-if="currentRecord.status === 'FAILED'" color="red">失败</a-tag>
                  <a-tag v-else>{{ currentRecord.status }}</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="开始时间">
                  {{ formatTimestamp(currentRecord.startTime) }}
                </a-descriptions-item>
                <a-descriptions-item label="结束时间">
                  {{ formatTimestamp(currentRecord.endTime) }}
                </a-descriptions-item>
                <a-descriptions-item label="执行时长">
                  <span v-if="currentRecord.startTime && currentRecord.endTime">
                    {{ ((currentRecord.endTime - currentRecord.startTime) / 1000).toFixed(2) }}秒
                  </span>
                  <span v-else>-</span>
                </a-descriptions-item>
                <a-descriptions-item label="总数据量">
                  {{ currentRecord.totalCount || 0 }}
                </a-descriptions-item>
                <a-descriptions-item label="成功数量">
                  {{ currentRecord.successCount || 0 }}
                </a-descriptions-item>
                <a-descriptions-item label="失败数量">
                  {{ currentRecord.failedCount || 0 }}
                </a-descriptions-item>
              </a-descriptions>
            </a-col>
            <a-col :span="20" class="detail-right">
              <div class="log-container">
                <div class="log-title">日志</div>
                <div class="log-content">{{ currentRecord.errorMsg || '-' }}</div>
              </div>
            </a-col>
          </a-row>
        </div>
      </a-spin>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, watch } from 'vue'
import { Message } from '@arco-design/web-vue'
import CanvasGlassCard from '@/components/CanvasGlassCard.vue'
import { 
  IconRefresh,
  IconEye,
  IconSearch
} from '@arco-design/web-vue/es/icon'
import { getSyncRecordList, getSyncRecordDetail, getTaskNameList } from '@/apis/system/data-sync'

// 同步记录列表
const syncRecordList = ref([])
// 加载状态
const loading = ref(false)
// 详情弹窗可见性
const detailVisible = ref(false)
// 详情加载状态
const detailLoading = ref(false)
// 当前查看的记录
const currentRecord = ref({})

// 搜索表单
const searchForm = reactive({
  taskName: ''
})

// 任务名称选项
const taskNameOptions = ref([])

// 表格列定义
const tableColumns = ref([
  { title: '任务名称', dataIndex: 'taskName', width: 150, ellipsis: true, tooltip: true },
  { title: '表名', dataIndex: 'tableName', width: 150, ellipsis: true, tooltip: true },
  { title: '状态', dataIndex: 'status', width: 100, slotName: 'status' },
  { title: '开始时间', dataIndex: 'startTime', width: 180, render: ({ record }) => formatTimestamp(record.startTime) },
  { title: '结束时间', dataIndex: 'endTime', width: 180, render: ({ record }) => formatTimestamp(record.endTime) },
  { title: '执行时长', dataIndex: 'duration', width: 100, slotName: 'duration' },
  { title: '总数据量', dataIndex: 'totalCount', width: 100 },
  { title: '成功数量', dataIndex: 'successCount', width: 100 },
  { title: '失败数量', dataIndex: 'failedCount', width: 100 },
  { title: '操作', dataIndex: 'action', width: 120, slotName: 'action', align: 'center', fixed: 'right' }
])

// 分页配置
const pagination = ref({
  current: 1,
  pageSize: 10,
  total: 0,
  showTotal: true,
  showJumper: true,
  hideOnSinglePage: false
})

// 格式化时间戳
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-'
  const date = new Date(timestamp)
  return date.toLocaleString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit', 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  }).replace(/\//g, '-')
}

// 获取任务名称列表
const fetchTaskNameList = async () => {
  try {
    const response = await getTaskNameList()
    if (response.code === 0) {
      taskNameOptions.value = response.data
    } else {
      Message.error(response.message || '获取任务名称列表失败')
    }
  } catch (error) {
    Message.error('获取任务名称列表失败: ' + error.message)
    console.error('获取任务名称列表失败:', error)
  }
}

// 获取同步记录列表
const fetchSyncRecordList = async () => {
  try {
    loading.value = true
    const params = {
      current: pagination.value.current,
      size: pagination.value.pageSize,
      taskName: searchForm.taskName
    }
    const response = await getSyncRecordList(params)
    if (response.code === 0) {
      syncRecordList.value = response.data.records
      pagination.value.total = response.data.total
    } else {
      Message.error(response.message || '获取同步记录列表失败')
    }
  } catch (error) {
    Message.error('获取同步记录列表失败: ' + error.message)
    console.error('获取同步记录列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 处理搜索
const handleSearch = () => {
  pagination.value.current = 1
  fetchSyncRecordList()
}

// 重置搜索
const resetSearch = () => {
  searchForm.taskName = ''
  handleSearch()
}

// 查看详情
const viewDetail = async (record) => {
  detailVisible.value = true
  detailLoading.value = true
  try {
    const response = await getSyncRecordDetail(record.id)
    if (response.code === 0) {
      currentRecord.value = response.data
    } else {
      Message.error(response.message || '获取同步记录详情失败')
    }
  } catch (error) {
    Message.error('获取同步记录详情失败: ' + error.message)
    console.error('获取同步记录详情失败:', error)
  } finally {
    detailLoading.value = false
  }
}

// 处理页码变化
const handlePageChange = (current) => {
  pagination.value.current = current
  fetchSyncRecordList()
}

// 处理页面大小变化
const handlePageSizeChange = (pageSize) => {
  pagination.value.pageSize = pageSize
  pagination.value.current = 1
  fetchSyncRecordList()
}

// 组件挂载时获取数据
onMounted(() => {
  fetchTaskNameList()
  fetchSyncRecordList()
})
</script>

<style scoped>
.sync-record-container {
  padding: 20px;
}

.glass-card__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
}

.button-table-option {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}

.spin-container {
  height: calc(100vh - 200px);
  width: 100%;
}

.detail-container {
  padding: 20px;
  height: 100%;
}

.detail-row {
  margin: 0;
  height: 100%;
}

.detail-left {
  padding-right: 10px;
  height: 100%;
}

.detail-right {
  padding-left: 10px;
  height: 100%;
}

.log-container {
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.log-title {
  padding: 12px 16px;
  background-color: #f2f3f5;
  border-bottom: 1px solid #e5e5e5;
  font-weight: 500;
}

.log-content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>